import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BuildingLibraryIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ChartBarIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  FireIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

interface Proposal {
  id: string;
  title: string;
  description: string;
  proposer: {
    address: string;
    avatar: string;
  };
  status: 'ACTIVE' | 'PENDING' | 'PASSED' | 'REJECTED' | 'EXECUTED';
  votesFor: number;
  votesAgainst: number;
  totalVotes: number;
  quorum: number;
  endTime: Date;
  category: string;
  createdAt: Date;
}

const mockProposals: Proposal[] = [
  {
    id: '1',
    title: 'Reduce Platform Fees to 2%',
    description: 'Proposal to reduce the base platform fee from 2.5% to 2% to attract more users and increase volume.',
    proposer: { address: '0x1234...5678', avatar: 'https://picsum.photos/seed/proposer1/100/100' },
    status: 'ACTIVE',
    votesFor: 1250000,
    votesAgainst: 450000,
    totalVotes: 1700000,
    quorum: 1000000,
    endTime: new Date(Date.now() + 86400000 * 2),
    category: 'Economics',
    createdAt: new Date(Date.now() - 86400000 * 5),
  },
  {
    id: '2',
    title: 'Add Support for Polygon Network',
    description: 'Integrate Polygon network for lower gas fees and faster transactions on NFT auctions.',
    proposer: { address: '0xABCD...EFGH', avatar: 'https://picsum.photos/seed/proposer2/100/100' },
    status: 'ACTIVE',
    votesFor: 890000,
    votesAgainst: 120000,
    totalVotes: 1010000,
    quorum: 1000000,
    endTime: new Date(Date.now() + 86400000 * 4),
    category: 'Technical',
    createdAt: new Date(Date.now() - 86400000 * 3),
  },
  {
    id: '3',
    title: 'Increase Staking APY for Platinum Tier',
    description: 'Increase the APY for Platinum tier stakers from 30% to 35% to incentivize long-term holding.',
    proposer: { address: '0x9876...5432', avatar: 'https://picsum.photos/seed/proposer3/100/100' },
    status: 'PASSED',
    votesFor: 2100000,
    votesAgainst: 300000,
    totalVotes: 2400000,
    quorum: 1000000,
    endTime: new Date(Date.now() - 86400000 * 2),
    category: 'Economics',
    createdAt: new Date(Date.now() - 86400000 * 10),
  },
  {
    id: '4',
    title: 'Partner with OpenSea for Cross-Listing',
    description: 'Establish a partnership with OpenSea to allow cross-platform NFT listings.',
    proposer: { address: '0x5555...6666', avatar: 'https://picsum.photos/seed/proposer4/100/100' },
    status: 'REJECTED',
    votesFor: 450000,
    votesAgainst: 1200000,
    totalVotes: 1650000,
    quorum: 1000000,
    endTime: new Date(Date.now() - 86400000 * 5),
    category: 'Partnership',
    createdAt: new Date(Date.now() - 86400000 * 15),
  },
  {
    id: '5',
    title: 'Launch NFT Lending Protocol',
    description: 'Develop and launch an NFT-collateralized lending protocol within the TEMPLUM ecosystem.',
    proposer: { address: '0x7777...8888', avatar: 'https://picsum.photos/seed/proposer5/100/100' },
    status: 'PENDING',
    votesFor: 0,
    votesAgainst: 0,
    totalVotes: 0,
    quorum: 1000000,
    endTime: new Date(Date.now() + 86400000 * 7),
    category: 'Feature',
    createdAt: new Date(Date.now() - 86400000 * 1),
  },
];

const statusConfig = {
  ACTIVE: { color: 'text-green-400', bgColor: 'bg-green-500/10', label: 'Active' },
  PENDING: { color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', label: 'Pending' },
  PASSED: { color: 'text-blue-400', bgColor: 'bg-blue-500/10', label: 'Passed' },
  REJECTED: { color: 'text-red-400', bgColor: 'bg-red-500/10', label: 'Rejected' },
  EXECUTED: { color: 'text-purple-400', bgColor: 'bg-purple-500/10', label: 'Executed' },
};

const GovernancePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Mock governance stats
  const stats = {
    totalProposals: 156,
    activeProposals: 5,
    totalVoters: 12847,
    yourVotingPower: 1500,
    treasuryBalance: 5420000,
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatTimeLeft = (endTime: Date) => {
    const diff = endTime.getTime() - Date.now();
    if (diff <= 0) return 'Ended';
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const getVotePercentage = (votesFor: number, totalVotes: number) => {
    if (totalVotes === 0) return 0;
    return (votesFor / totalVotes) * 100;
  };

  const filteredProposals = mockProposals.filter((proposal) => {
    if (searchQuery && !proposal.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (statusFilter !== 'all' && proposal.status !== statusFilter) {
      return false;
    }
    if (categoryFilter !== 'all' && proposal.category !== categoryFilter) {
      return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">
                <span className="text-gradient">DAO Governance</span>
              </h1>
              <p className="text-gray-400 text-lg mt-2">
                Shape the future of TEMPLUM through decentralized governance
              </p>
            </div>
            <Link to="/governance/create" className="btn-primary flex items-center space-x-2">
              <PlusIcon className="h-5 w-5" />
              <span>Create Proposal</span>
            </Link>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12"
        >
          <div className="glass-card p-5">
            <div className="flex items-center space-x-3 mb-2">
              <DocumentTextIcon className="h-5 w-5 text-primary-400" />
              <span className="text-sm text-gray-400">Total Proposals</span>
            </div>
            <p className="text-2xl font-bold">{stats.totalProposals}</p>
          </div>

          <div className="glass-card p-5">
            <div className="flex items-center space-x-3 mb-2">
              <FireIcon className="h-5 w-5 text-green-400" />
              <span className="text-sm text-gray-400">Active Now</span>
            </div>
            <p className="text-2xl font-bold text-green-400">{stats.activeProposals}</p>
          </div>

          <div className="glass-card p-5">
            <div className="flex items-center space-x-3 mb-2">
              <UserGroupIcon className="h-5 w-5 text-blue-400" />
              <span className="text-sm text-gray-400">Total Voters</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(stats.totalVoters)}</p>
          </div>

          <div className="glass-card p-5">
            <div className="flex items-center space-x-3 mb-2">
              <ArrowTrendingUpIcon className="h-5 w-5 text-purple-400" />
              <span className="text-sm text-gray-400">Your Voting Power</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">{formatNumber(stats.yourVotingPower)}</p>
          </div>

          <div className="glass-card p-5">
            <div className="flex items-center space-x-3 mb-2">
              <BuildingLibraryIcon className="h-5 w-5 text-amber-400" />
              <span className="text-sm text-gray-400">Treasury</span>
            </div>
            <p className="text-2xl font-bold">${formatNumber(stats.treasuryBalance)}</p>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col md:flex-row gap-4 mb-8"
        >
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search proposals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="PENDING">Pending</option>
            <option value="PASSED">Passed</option>
            <option value="REJECTED">Rejected</option>
            <option value="EXECUTED">Executed</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
          >
            <option value="all">All Categories</option>
            <option value="Economics">Economics</option>
            <option value="Technical">Technical</option>
            <option value="Feature">Feature</option>
            <option value="Partnership">Partnership</option>
          </select>
        </motion.div>

        {/* Proposals List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          {filteredProposals.map((proposal, index) => {
            const statusConf = statusConfig[proposal.status];
            const votePercentage = getVotePercentage(proposal.votesFor, proposal.totalVotes);
            const quorumMet = proposal.totalVotes >= proposal.quorum;

            return (
              <motion.div
                key={proposal.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Link
                  to={`/governance/${proposal.id}`}
                  className="block glass-card p-6 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConf.bgColor} ${statusConf.color}`}>
                          {statusConf.label}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-dark-700 text-gray-400">
                          {proposal.category}
                        </span>
                        {proposal.status === 'ACTIVE' && (
                          <span className="flex items-center space-x-1 text-xs text-gray-400">
                            <ClockIcon className="h-4 w-4" />
                            <span>{formatTimeLeft(proposal.endTime)}</span>
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-semibold mb-2 hover:text-primary-400 transition-colors">
                        {proposal.title}
                      </h3>
                      <p className="text-gray-400 text-sm line-clamp-2">{proposal.description}</p>
                    </div>
                  </div>

                  {/* Voting Progress */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1 text-green-400">
                          <CheckCircleIcon className="h-4 w-4" />
                          <span>For: {formatNumber(proposal.votesFor)}</span>
                        </span>
                        <span className="flex items-center space-x-1 text-red-400">
                          <XCircleIcon className="h-4 w-4" />
                          <span>Against: {formatNumber(proposal.votesAgainst)}</span>
                        </span>
                      </div>
                      <span className={`text-sm ${quorumMet ? 'text-green-400' : 'text-yellow-400'}`}>
                        {quorumMet ? 'Quorum met' : `${formatNumber(proposal.totalVotes)}/${formatNumber(proposal.quorum)} quorum`}
                      </span>
                    </div>
                    <div className="w-full h-3 bg-dark-700 rounded-full overflow-hidden flex">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-green-400"
                        style={{ width: `${votePercentage}%` }}
                      />
                      <div
                        className="h-full bg-gradient-to-r from-red-500 to-red-400"
                        style={{ width: `${100 - votePercentage}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                      <span>{votePercentage.toFixed(1)}% For</span>
                      <span>{(100 - votePercentage).toFixed(1)}% Against</span>
                    </div>
                  </div>

                  {/* Proposer */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/5">
                    <div className="flex items-center space-x-3">
                      <img
                        src={proposal.proposer.avatar}
                        alt=""
                        className="w-8 h-8 rounded-full"
                      />
                      <div>
                        <p className="text-sm text-gray-400">Proposed by</p>
                        <p className="text-sm font-medium">{proposal.proposer.address}</p>
                      </div>
                    </div>
                    {proposal.status === 'ACTIVE' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            // Handle vote for
                          }}
                          className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                        >
                          Vote For
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            // Handle vote against
                          }}
                          className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                        >
                          Vote Against
                        </button>
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Empty State */}
        {filteredProposals.length === 0 && (
          <div className="text-center py-20">
            <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No proposals found</h3>
            <p className="text-gray-400 mb-6">Try adjusting your filters or create a new proposal</p>
            <Link to="/governance/create" className="btn-primary">
              Create Proposal
            </Link>
          </div>
        )}

        {/* Governance Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Voting Requirements</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span>Minimum 1,000 NDH staked to vote</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span>Voting power = staked NDH amount</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span>1M NDH quorum required</span>
              </li>
            </ul>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Proposal Creation</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
                <span>Silver tier or higher required</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
                <span>10,000 NDH minimum stake</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
                <span>7-day voting period</span>
              </li>
            </ul>
          </div>

          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Execution</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-amber-400 flex-shrink-0" />
                <span>48-hour timelock after passing</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-amber-400 flex-shrink-0" />
                <span>&gt;50% approval needed</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-amber-400 flex-shrink-0" />
                <span>Anyone can execute after timelock</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GovernancePage;
